const DIDOMI_CPRA_COOKIE_NAME = 'didomi_token_cpra';

const getConsentSnapshot = status => ({
  created: status.created,
  vendors: JSON.stringify(
    Object.keys(status.vendors || {})
      .filter(id => status.vendors[id]?.enabled === true)
      .sort()
  ),
});

const getCookieCreated = cookie => {
  try {
    const { created } = JSON.parse(window.atob(decodeURIComponent(cookie)));
    if (typeof created !== 'string' || !Number.isFinite(Date.parse(created))) {
      throw new TypeError('Invalid CPRA consent creation date');
    }
    return created;
  } catch {
    console.warn('CMP: Unable to decode the existing CPRA consent cookie.');
    return null;
  }
};

export const createCpraConsentTracker = notifyConsentChange => {
  const cookieName =
    window.didomiConfig?.cookies?.didomiTokenCookieName ||
    DIDOMI_CPRA_COOKIE_NAME;
  const cookiePrefix = `${cookieName}=`;
  // Didomi persists a new CPRA record before its ready callbacks run.
  const initialCookie = window.document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(cookiePrefix))
    ?.slice(cookiePrefix.length);
  let sdkReady = false;
  let noticeShown = false;
  let consent = null;
  let initialNotificationPending = false;

  const initializeConsent = status => {
    if (consent) {
      return;
    }
    consent = getConsentSnapshot(status);
    const previousCreated =
      initialCookie === undefined ? null : getCookieCreated(initialCookie);
    initialNotificationPending =
      initialCookie === undefined ||
      (previousCreated !== null && previousCreated !== status.created);
  };

  const notifyInitialConsent = status => {
    if (!sdkReady || !noticeShown) {
      return;
    }
    const snapshot = getConsentSnapshot(status);
    const newRecord = consent && consent.created !== snapshot.created;
    if (!initialNotificationPending && !newRecord) {
      return;
    }
    initialNotificationPending = false;
    consent = snapshot;
    notifyConsentChange();
  };

  return {
    onReady(status, noticeAlreadyShown = false) {
      sdkReady = true;
      noticeShown = noticeShown || noticeAlreadyShown;
      initializeConsent(status);
      notifyInitialConsent(status);
    },
    onNoticeShown(status) {
      noticeShown = true;
      notifyInitialConsent(status);
    },
    shouldNotifyConsentChange(status) {
      if (!sdkReady) {
        return false;
      }
      const snapshot = getConsentSnapshot(status);
      // Record-only replacements remain pending until their notice is shown.
      if (consent && consent.vendors === snapshot.vendors) {
        return false;
      }
      consent = snapshot;
      initialNotificationPending = false;
      return true;
    },
  };
};
